import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { localManagedVenueSource } from '@/lib/venue/managedVenue'
import { venueHomePath } from '@/routes/postLogin'

interface VenueRegisterCompleteState {
  venueId: string
  name: string
  nextSteps: string[]
}

/**
 * `nextSteps` 안내 2단계 카드 문구(api-spec §1). forward-only — 모르는 값은 카드를 그리지 않는다
 * (안전한 기본 동작, 문구를 클라이언트가 지어내지 않는다).
 */
const NEXT_STEP_COPY: Record<string, { title: string; description: string }> = {
  OPERATION_VERIFICATION: {
    title: '운영 인증',
    description: '인증을 완료해야 구장 목록과 검색에 노출돼요.',
  },
  COURT_REGISTRATION: {
    title: '코트·요금 등록',
    description: '코트별 이용가격까지 채우면 예약 기능을 켤 수 있어요.',
  },
}

function isCompleteState(value: unknown): value is VenueRegisterCompleteState {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record.venueId === 'string' &&
    /^[1-9]\d*$/.test(record.venueId) &&
    typeof record.name === 'string' &&
    Array.isArray(record.nextSteps)
  )
}

/**
 * 구장 등록 완료 안내 — Figma `구장 등록 완료`(14039-39426).
 *
 * 🚨 응답값만으로 그린다(재조회 없음, design.md §2 "응답(201)" 참조). `location.state` 로 직접
 * 전달받으며, 새로고침 등으로 그 값이 없으면 구장 선택 화면으로 되돌린다.
 */
export function VenueRegisterCompletePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as unknown

  if (!isCompleteState(state)) {
    return <Navigate to="/venues/select" replace />
  }

  const { venueId, name, nextSteps } = state
  const steps = nextSteps.map((step) => NEXT_STEP_COPY[step]).filter((step): step is { title: string; description: string } => Boolean(step))

  function startManaging() {
    localManagedVenueSource.remember(venueId)
    navigate(venueHomePath(venueId), { replace: true })
  }

  return (
    <div className="mx-auto max-w-[560px] px-6 py-16">
      <Card>
        <CardBody className="space-y-5 text-center">
          <div>
            <h2 className="text-lg font-semibold text-ink-700">구장 등록이 완료됐어요</h2>
            <p className="mt-1 text-sm text-slateish-600">
              <span className="font-medium text-ink-700">{name}</span> 이(가) 계정에 연결됐습니다.
            </p>
          </div>

          {steps.length > 0 ? (
            <div className="space-y-3 text-left">
              {steps.map((step) => (
                <div key={step.title} className="rounded-lg bg-slateish-50 px-4 py-3">
                  <p className="text-sm font-medium text-ink-700">{step.title}</p>
                  <p className="mt-0.5 text-sm text-slateish-600">{step.description}</p>
                </div>
              ))}
            </div>
          ) : null}

          <Button block onClick={startManaging}>
            구장 관리 시작하기
          </Button>
        </CardBody>
      </Card>
    </div>
  )
}
